/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC } from "react";

import { Config } from "../config/Config";

import styles from "./MeetSettings.module.css";

interface Feature {
  name: string;
  desc: string;
}

interface FeatureGroup {
  title: string;
  items: Feature[];
}

const GROUPS: FeatureGroup[] = [
  {
    title: "Audio & Voice",
    items: [
      { name: "Studio Stereo Audio", desc: "Music-grade Opus stereo on every call, no caps." },
      { name: "5-Band Equalizer", desc: "Shape your mic with a local EQ (bass to treble)." },
      { name: "Voice Changer", desc: "Deep, chipmunk or robot voice, processed on-device." },
      { name: "Noise Gate", desc: "Silence background noise below a threshold." },
      { name: "Echo Cancellation", desc: "Built-in acoustic echo removal." },
      { name: "Noise Suppression", desc: "WebRTC noise suppression on the mic." },
      { name: "Push-to-Talk", desc: "Stay muted, hold a hotkey to speak." },
      { name: "Live Captions", desc: "Real-time speech-to-text via the Web Speech API." },
      { name: "Auto Gain Control", desc: "Even out quiet and loud moments automatically." },
      { name: "In-Call Sound Effects", desc: "Optional local reaction & UI sounds." },
      { name: "Device Selection", desc: "Pick your mic, speaker and camera live." },
      { name: "Loudspeaker / Earpiece", desc: "Toggle output on mobile devices." },
    ],
  },
  {
    title: "Video & Visual",
    items: [
      { name: "Up to 11K Video", desc: "Scales from 360p to 8K/11K on capable cameras." },
      { name: "8K UHD Support", desc: "Crystal-clear 4320p when your hardware allows." },
      { name: "Background Blur", desc: "Blur your real background locally." },
      { name: "Video Filters", desc: "Brightness, contrast, sepia, grayscale, invert." },
      { name: "Virtual Background", desc: "Replace your background with an image." },
      { name: "Picture-in-Picture", desc: "Pop the call out as a floating window." },
      { name: "Fullscreen Mode", desc: "Take the whole call to a borderless full screen." },
      { name: "Dark & Light Themes", desc: "Switch or auto-match your system." },
      { name: "High Contrast Theme", desc: "Accessible high-contrast colour scheme." },
      { name: "Custom Themes", desc: "Tint the UI with your own accent colours." },
      { name: "UI Scale", desc: "Scale the whole interface to your liking." },
      { name: "Large Tap Targets", desc: "Bigger buttons for touch & motor access." },
      { name: "Reduce Motion", desc: "Minimise non-essential animations." },
      { name: "Grid & Spotlight", desc: "Switch between grid and speaker-focused layouts." },
      { name: "Raise Hand", desc: "Signal the speaker without unmuting." },
    ],
  },
  {
    title: "Recording & Sharing",
    items: [
      { name: "Screen Sharing", desc: "Share your screen at up to 24 Mbps, with audio." },
      { name: "Whiteboard", desc: "Collaborative canvas shared over DataChannels." },
      { name: "In-Call Polls", desc: "Create quick polls, results stay local." },
      { name: "P2P File Transfer", desc: "Send files peer-to-peer, no upload server." },
      { name: "Meeting Timer", desc: "Track call duration with a visible timer." },
      { name: "Reactions", desc: "Send floating reactions in-call." },
      { name: "Copy Invite Link", desc: "Share the room link in one click." },
      { name: "QR Code Invite", desc: "Scan to join from another device." },
    ],
  },
  {
    title: "Privacy & Security",
    items: [
      { name: "End-to-End Encryption", desc: "Calls are encrypted via MatrixRTC." },
      { name: "Local TOTP 2FA", desc: "Time-based one-time codes, no external auth." },
      { name: "Session Management", desc: "Review and revoke your Nevai sessions." },
      { name: "Local Data Backup", desc: "Export/import your settings as a file." },
      { name: "Status & Presence", desc: "Control how your availability is broadcast." },
      { name: "Blur by Default", desc: "Start every call with background blur on." },
    ],
  },
  {
    title: "Connection & Productivity",
    items: [
      { name: "Custom LiveKit URL", desc: "Point the SFU at your own server." },
      { name: "MatrixRTC Mode", desc: "Legacy / Compatibility / Matrix 2.0." },
      { name: "Connection Stats", desc: "Live bitrate, ping and quality overlay." },
      { name: "Data Saver", desc: "Force 360p @ 15fps to save bandwidth." },
      { name: "Local Call History", desc: "A private log of past calls on this device." },
      { name: "Maximum Quality", desc: "Up to 8K/11K video + studio stereo audio." },
      { name: "Keyboard Shortcuts", desc: "Mute, camera, raise hand and more." },
      { name: "Notifications", desc: "Control in-call sound & desktop alerts." },
      { name: "Adaptive Bitrate", desc: "Auto-tune quality to your connection." },
      { name: "Hotkey Overlay", desc: "Press ? to see every keyboard shortcut." },
      { name: "Network Resilience", desc: "Recover fast from Wi-Fi hiccups." },
    ],
  },
  {
    title: "Call Types",
    items: [
      { name: "Meet Call", desc: "Full video + audio meeting with screen sharing." },
      { name: "Coffee Call", desc: "Voice-only call, no camera — light & private." },
      { name: "Video Call (HD)", desc: "High-quality HD video with no limits & a timer." },
      { name: "One-Tap Switch", desc: "Pick a call type from the home screen." },
      { name: "Delete Any Call", desc: "Remove a call from your list or end it for everyone." },
      { name: "Recent Calls", desc: "Jump back into past rooms instantly." },
      { name: "Maximum Quality Toggle", desc: "One click for 4K/8K/11K + studio audio." },
    ],
  },
  {
    title: "Invite & Join",
    items: [
      { name: "Shareable Room Link", desc: "Anyone with the link can join." },
      { name: "QR Code Join", desc: "Scan a code to open the room." },
      { name: "One-Tap Join", desc: "Jump straight into a call from the list." },
      { name: "Guest Friendly", desc: "No app install — opens in any browser." },
      { name: "Widget Mode", desc: "Embed a call inside another app." },
    ],
  },
  {
    title: "Reliability & Comfort",
    items: [
      { name: "Auto-Reconnect", desc: "Seamlessly recover from dropped connections." },
      { name: "Wake Lock", desc: "Keep the screen awake while you talk." },
      { name: "Network Resilience", desc: "Handles Wi-Fi switches and hiccups." },
      { name: "Adaptive Bitrate", desc: "Auto-tunes quality to your connection." },
      { name: "Connection Stats", desc: "Live bitrate, ping and packet-loss overlay." },
      { name: "Keyboard Shortcuts", desc: "Mute, camera, share and more — press ?." },
      { name: "Hotkey Overlay", desc: "See every shortcut on demand." },
      { name: "Notifications", desc: "Control in-call sound & desktop alerts." },
    ],
  },
  {
    title: "Power & Unlimited",
    items: [
      { name: "Unlimited Duration", desc: "No time caps — talk as long as you like." },
      { name: "Unlimited Participants", desc: "No hard cap on how many can join a room." },
      { name: "Unlimited Recording", desc: "Record locally as many calls as you want." },
      { name: "Up to 11K Video", desc: "Scales from 360p up to 8K/11K on capable cameras." },
      { name: "8K UHD Support", desc: "Crystal-clear 4320p when your hardware allows." },
      { name: "Studio Stereo Audio", desc: "Music-grade Opus stereo for crisp sound." },
      { name: "Zero Throttling", desc: "Quality follows your connection, not a paywall." },
      { name: "Self-Hostable", desc: "Run your own backend for total control." },
    ],
  },
  {
    title: "Free, Private & Local",
    items: [
      { name: "100% Free", desc: "Every feature here is free, forever." },
      { name: "No External API", desc: "Nothing calls out to a paid cloud service." },
      { name: "No AI Processing", desc: "Audio/video are never sent to an AI model." },
      { name: "Runs In Your Browser", desc: "WebRTC + WASM crypto, no install needed." },
      { name: "Open Source", desc: "AGPL-3.0 — audit or modify the code." },
      { name: "Local-Only Storage", desc: "History, themes & settings stay on this device." },
      { name: "End-to-End Encrypted", desc: "Calls secured via MatrixRTC." },
      { name: "No Tracking", desc: "No analytics unless you explicitly opt in." },
    ],
  },
];

export const FeaturesSettingsTab: FC = () => {
  const homeserver = Config.get().default_server_config?.["m.homeserver"];
  return (
    <div>
      <div className={styles.serverBox}>
        <span className={styles.serverLabel}>Server (read-only)</span>
        <span className={styles.serverValue}>{homeserver?.server_name ?? "—"}</span>
        <span className={styles.small}>{homeserver?.base_url ?? "—"}</span>
      </div>
      <div className={styles.banner}>
        <span className={styles.bannerItem}>100% Free</span>
        <span className={styles.bannerItem}>No API</span>
        <span className={styles.bannerItem}>No AI</span>
        <span className={styles.bannerItem}>Unlimited</span>
        <span className={styles.bannerItem}>Private &amp; Local</span>
      </div>
      <h3 className={styles.sectionTitle}>All Features</h3>
      <p className={styles.note}>
        Every feature listed below is implemented and runs{" "}
        <strong>100% locally in your browser</strong> — no paid AI, no external
        processing, no accounts beyond your Nevai homeserver.
      </p>

      {GROUPS.map((group) => (
        <div key={group.title} className={styles.section}>
          <h3 className={styles.sectionTitle}>{group.title}</h3>
          <div className={styles.featureGrid}>
            {group.items.map((f) => (
              <div key={f.name} className={styles.featureCard}>
                <span className={styles.featureName}>{f.name}</span>
                <span className={styles.featureDesc}>{f.desc}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
